import { ColdIcon, WarmIcon, HotIcon, RemittanceIcon, InsuranceIcon, FinancingIcon, ReferralIcon, ReKYCIcon } from "../../lead-inventory/icons";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LeadTableActions from "./accIncomingTableAction";
import AssignFarmerModal from "../../account-distribution/assigned/AssignFarmerModal.jsx";
import SidebarTabsContents from "../../lead-inventory/all/sidebar-components/leadSidebar.jsx";
import { toast } from "react-toastify";

const LeadInventoryTableAll = ({ data = [], onSelectionChange, clearOnSome = true, farmers = [], onRefresh }) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [leadID, setLeadID] = useState(null);
  const [leadPipelineStage, setLeadPipelineStage] = useState(null);
  const [leadStatus, setLeadStatus] = useState(null);
  const [leadInterestLevel, setLeadInterestLevel] = useState(null);
  const [leadName, setLeadName] = useState(null);
  const [isUpdatingFarmerId, setIsUpdatingFarmerId] = useState(false);
  const [selectedFarmerId, setSelectedFarmerId] = useState("");
  const [showFarmerSelector, setShowFarmerSelector] = useState(false);

  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignLeadId, setAssignLeadId] = useState(null);
  const [assignAccountName, setAssignAccountName] = useState("");
  const [isAssignSubmitting, setIsAssignSubmitting] = useState(false);

  // ===== Selection state (Gmail-style) =====
  const [selected, setSelected] = useState(() => new Set());
  const lastSelectedIndex = useRef(null);
  const headerCheckboxRef = useRef(null);

  const allIds = useMemo(() => data.map((d) => d.lead_id), [data]);
  const total = data.length;
  const selectedCount = selected.size;
  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && selectedCount < total;

  const openAssignModal = (leadId) => {
    const row = data.find((d) => d.lead_id === leadId);
    setAssignLeadId(leadId);
    setAssignAccountName(row?.name || "");
    setIsAssignOpen(true);
  };

  const handleAssignFarmerSingle = async (farmerId) => {
    try {
      setIsAssignSubmitting(true);
      await updateLeadFarmerId(assignLeadId, farmerId);
      setIsAssignOpen(false);
      
      // Use the refresh function from parent instead of window.location.reload()
      if (onRefresh) {
        await onRefresh();
      }
    } catch (e) {
      toast.error(e.message || 'Failed to assign farmer');
    } finally {
      setIsAssignSubmitting(false);
    }
  };

  // Expose selection upwards if needed
  useEffect(() => {
    onSelectionChange?.(Array.from(selected));
  }, [selected, onSelectionChange]);

  // Keep header checkbox indeterminate state in sync
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  // Function to update farmer_id for a single lead
  const updateLeadFarmerId = async (leadId, farmerId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_URL}/leads/${leadId}/farmer_id`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ farmer_id: farmerId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update farmer_id');
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating farmer_id for lead ${leadId}:`, error);
      throw error;
    }
  };

  // Function to update farmer_id for all selected leads
  const updateSelectedLeadsFarmerId = async (selectedLeadIds, farmerId) => {
    if (!farmerId || selectedLeadIds.length === 0) {
      return;
    }

    setIsUpdatingFarmerId(true);
    const results = {
      successful: [],
      failed: [],
    };

    try {
      // Process updates in parallel with a reasonable concurrency limit
      const batchSize = 5;
      for (let i = 0; i < selectedLeadIds.length; i += batchSize) {
        const batch = selectedLeadIds.slice(i, i + batchSize);
        const batchPromises = batch.map(async (leadId) => {
          try {
            const result = await updateLeadFarmerId(leadId, farmerId);
            results.successful.push({ leadId, result });
          } catch (error) {
            results.failed.push({ leadId, error: error.message });
          }
        });

        await Promise.all(batchPromises);
      }

      // Log results
      console.log(`Successfully updated ${results.successful.length} leads`);
      if (results.failed.length > 0) {
        console.error(`Failed to update ${results.failed.length} leads:`, results.failed);
      }

      // Show success/error message to user
      if (results.successful.length > 0) {
        const successMsg = `Successfully assigned farmer to ${results.successful.length} lead${results.successful.length > 1 ? 's' : ''}`;
        const failMsg = results.failed.length > 0 ? `. Failed to update ${results.failed.length} lead${results.failed.length > 1 ? 's' : ''}.` : '';
        toast.success(successMsg + failMsg);
      }

    } catch (error) {
      console.error('Error in batch update:', error);
      toast.error('An error occurred during batch update');
    } finally {
      setIsUpdatingFarmerId(false);
    }

    return results;
  };

  const emitSelection = useCallback((newSet) => setSelected(newSet), []);

  // Function to manually assign farmer to selected leads
  const handleAssignFarmer = async () => {
    if (!selectedFarmerId || selected.size === 0) {
      toast.info('Please select leads and choose a farmer to assign');
      return;
    }

    const selectedLeadIds = Array.from(selected);
    await updateSelectedLeadsFarmerId(selectedLeadIds, parseInt(selectedFarmerId, 10));
    
    // Use the refresh function from parent instead of window.location.reload()
    if (onRefresh) {
      await onRefresh();
    }
    
    // Clear selection after successful assignment
    emitSelection(new Set());
    setShowFarmerSelector(false);
    setSelectedFarmerId("");
  };

  const updateSelection = useCallback(
    (ids, shouldSelect) => {
      const next = new Set(selected);
      ids.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
      emitSelection(next);
    },
    [selected, emitSelection]
  );

  const handleSelectAll = useCallback(() => {
    let next;

    if (clearOnSome) {
      // If anything is selected -> clear all, else select all
      if (selectedCount === 0) {
        next = new Set(allIds);
      } else {
        next = new Set();
      }
    } else {
      // Toggle strictly by "all selected"
      if (allSelected) {
        next = new Set();
      } else {
        next = new Set(allIds);
      }
    }

    emitSelection(next);
    lastSelectedIndex.current = null;
  }, [allIds, allSelected, clearOnSome, emitSelection, selectedCount]);

  const handleRowToggle = useCallback(
    (index, event) => {
      // event can be from checkbox or row click
      const row = data[index];
      if (!row) return;

      const id = row.lead_id;
      const currentlySelected = selected.has(id);
      const shouldSelect = !currentlySelected;

      if (event?.shiftKey && lastSelectedIndex.current !== null && lastSelectedIndex.current !== index) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        const rangeIds = data.slice(start, end + 1).map((r) => r.lead_id);
        updateSelection(rangeIds, shouldSelect);
      } else {
        updateSelection([id], shouldSelect);
      }

      lastSelectedIndex.current = index;
    },
    [data, selected, updateSelection]
  );

  // ===== Sidebar detail open =====
  const handleDetailClick = (leadID) => {
    setShowSidebar(true);
    setLeadID(leadID);
    setLeadPipelineStage(data.find((item) => item.lead_id === leadID)?.status);
    setLeadStatus(data.find((item) => item.lead_id === leadID)?.lead_status);
    setLeadInterestLevel(data.find((item) => item.lead_id === leadID)?.interest_level);
    setLeadName(data.find((item) => item.lead_id === leadID)?.name);
  };

  // Prevent row click when clicking checkbox / actions
  const isInteractive = (el) => {
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    return (
      tag === "input" ||
      tag === "button" ||
      tag === "svg" ||
      tag === "path" ||
      el.closest("button") ||
      el.closest("input")
    );
  };

  // ===== Styles & labels =====
  const leadSourceLabels = {
    0: "Raw Lead",
    1: "Fresh Lead",
    2: "Profile Completed",
    3: "Meeting Completion",
    4: "Onboarded",
    5: "Activated",
    11: "Tanked",
  };

  const getStatusStyle = (status) => {
    const styles = {
      0: "bg-gray-100 text-gray-600",
      1: "bg-[#FEFAD8] border-[#FEFAD8] text-black",
      2: "bg-[#BEC2FF] border-[#BEC2FF] text-[#262BC6]",
      3: "bg-[#E3F3FF] border-[#E3F3FF] text-[#00658E]",
      4: "bg-[#E7FFD6] border-[#E7FFD6] text-[#116D26]",
      5: "bg-[#E7FFD6] border-[#E7FFD6] text-[#116D26]",
      11: "bg-red-200 text-red-800",
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  const getLeadStatusStyle = (status) => {
    const styles = {
      Active: "bg-[#FEFAD8] border-[#FEFAD8] text-black",
      Ongoing: "bg-[#FFEEDC] border-[#FFEEDC] text-[#A96900]",
      Inactive: "bg-[#EEEEEE] border-[#EEEEEE] text-[#757575]",
      Tanked: "bg-[#757575] border-[#757575] text-[#EEEEEE]",
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  return (
    <>
      {/* Selection summary with farmer assignment controls */}
      {selectedCount > 0 && (
        <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-blue-900">
                {selectedCount} lead{selectedCount > 1 ? 's' : ''} selected
              </span>
              
              {isUpdatingFarmerId && (
                <span className="text-blue-600 text-sm">
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-1"></span>
                  Updating farmer IDs...
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {!showFarmerSelector ? (
                <button
                  onClick={() => setShowFarmerSelector(true)}
                  className="px-4 py-2 bg-yellow-200 text-black text-sm border-2 border-black-600 rounded hover:bg-yellow-300 transition-colors"
                  disabled={isUpdatingFarmerId}
                >
                  Assign Farmer
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedFarmerId}
                    onChange={(e) => setSelectedFarmerId(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isUpdatingFarmerId}
                  >
                    <option value="">Select Farmer</option>
                    {farmers.map((farmer) => (
                      <option key={farmer.id} value={farmer.id}>
                        {farmer.username}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    onClick={handleAssignFarmer}
                    disabled={!selectedFarmerId || isUpdatingFarmerId}
                    className="px-3 py-1 bg-yellow-200 text-black text-sm rounded hover:bg-yellow-300 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Assign
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowFarmerSelector(false);
                      setSelectedFarmerId("");
                    }}
                    className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
                    disabled={isUpdatingFarmerId}
                  >
                    Cancel
                  </button>
                </div>
              )}

              <button
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                onClick={() => {
                  emitSelection(new Set());
                  lastSelectedIndex.current = null;
                  setShowFarmerSelector(false);
                  setSelectedFarmerId("");
                }}
                disabled={isUpdatingFarmerId}
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="">
        <table className="min-w-full bg-white border-b border-gray-200 table-fixed">
          <thead>
            <tr className="text-gray-400 border-t">
              {/* NEW: select-all checkbox column */}
              <th className="p-4 border-b text-center w-[4%]">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  aria-checked={allSelected ? "true" : someSelected ? "mixed" : "false"}
                  className="h-4 w-4"
                  disabled={isUpdatingFarmerId}
                />
              </th>
              <th className="p-4 border-b text-center w-[5%]">ID</th>
              <th className="p-4 border-b text-center w-[5%]">Product</th>
              <th className="p-4 border-b text-left w-[25%]">Name</th>
              <th className="p-4 border-b text-center w-[10%]">Lead Status</th>
              <th className="p-4 border-b text-center w-[20%]">Pipeline Stage</th>
              <th className="p-4 border-b text-center w-[15%]">Lead Source</th>
              <th className="p-4 border-b text-center w-[15%]">Last Interacted</th>
              <th className="p-4 border-b text-center w-[3%]">Hunter</th>
              <th className="p-4 border-b text-center w-[2%]">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => {
                const isSelected = selected.has(item.lead_id);
                return (
                  <tr
                    key={`row-${item.lead_id}-${index}`}
                    className={`hover:bg-[#F7F7F7] ${isSelected ? "bg-blue-50" : ""}`}
                    onClick={(e) => {
                      if (isInteractive(e.target)) return;
                      // don't open sidebar when clicking controls
                      handleDetailClick(item.lead_id);
                    }}
                  >
                    {/* NEW: row checkbox */}
                    <td className="p-4 border-b text-center w-[4%]" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          // Pass nativeEvent for proper shift detection across browsers
                          handleRowToggle(index, e.nativeEvent || e);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isUpdatingFarmerId}
                      />
                    </td>
                    <td className="p-4 border-b text-gray-500 text-center w-[5%]">{item.lead_id}</td>
                    <td className="p-4 border-b text-center w-[5%]">
                      {item.products?.includes("Remittance") && (
                        <img src={RemittanceIcon} alt="Remittance" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products?.includes("Insurance") && (
                        <img src={InsuranceIcon} alt="Insurance" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products?.includes("Financing") && (
                        <img src={FinancingIcon} alt="Financing" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products?.includes("Referral") && (
                        <img src={ReferralIcon} alt="Referral" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products?.includes("Re-KYC") && (
                        <img src={ReKYCIcon} alt="ReKYC" className="inline-block w-6 h-6 mr-2" />
                      )}
                    </td>
                    <td className="p-4 border-b font-bold text-left w-[28%]">{item.name}</td>
                    <td className="p-4 border-b text-center w-[10%]">
                      <span
                        className={`w-[95px] h-[34px] pt-2 px-4 pb-2 gap-2 rounded-full border font-bold ${getLeadStatusStyle(
                          item.lead_status
                        )}`}
                      >
                        {item.lead_status}
                      </span>
                    </td>
                    <td className="p-4 border-b text-center w-[20%]">
                      <span
                        className={`w-[95px] h-[34px] pt-2 px-4 pb-2 gap-2 rounded-full border font-bold ${getStatusStyle(
                          item.status
                        )}`}
                      >
                        {leadSourceLabels[item.status] || "Unknown"}
                      </span>
                    </td>
                    <td className="p-4 border-b text-center w-[15%] font-bold">
                      <span className="mr-2">{item.lead_source}</span>
                      {item.interest_level === 1 && (
                        <img src={ColdIcon} alt="Cold" className="w-6 h-6 inline-block gap-1" />
                      )}
                      {item.interest_level === 2 && (
                        <img src={WarmIcon} alt="Warm" className="w-6 h-6 inline-block gap-1" />
                      )}
                      {item.interest_level === 3 && (
                        <img src={HotIcon} alt="Hot" className="w-6 h-6 inline-block gap-1" />
                      )}
                    </td>
                    <td className="p-4 border-b text-center w-[15%] font-bold">
                      {item.updated_time}
                      <span className="text-xs text-[#A96900] border-[#FFEEDC] bg-[#FFEEDC] w-2 h-2 p-1 ml-2 rounded-full border">
                        {item.last_interacted}
                      </span>
                    </td>
                    <td className="p-4 border-b text-center w-[3%] font-bold">
                      <div className="relative group inline-block">
                        <span className="bg-[#EEEEEE] w-10 h-10 py-2 px-3 rounded-full inline-flex items-center justify-center">
                          {item.hunter_name ? item.hunter_name[0].toUpperCase() : ""}
                        </span>
                        <div className="absolute hidden group-hover:block bg-gray-800 text-white text-sm rounded py-1 px-2 -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                          {item.hunter_name
                            ? item.hunter_name[0].toUpperCase() + item.hunter_name.slice(1)
                            : "No Owner"}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 border-b text-center w-[2%]" onClick={(e) => e.stopPropagation()}>
                      <LeadTableActions leadId={item.lead_id} onAssignFarmer={openAssignModal} />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={10} className="text-center py-4 text-gray-500 border-b">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AssignFarmerModal
        open={isAssignOpen}
        accountName={assignAccountName}
        farmers={farmers}
        onClose={() => setIsAssignOpen(false)}
        onSubmit={handleAssignFarmerSingle}
        isSubmitting={isAssignSubmitting}
      />

      <SidebarTabsContents
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        leadID={leadID}
        leadPipelineStage={leadPipelineStage}
        leadStatus={leadStatus}
        leadInterestLevel={leadInterestLevel}
        leadName={leadName}
      />
    </>
  );
};

export default LeadInventoryTableAll;